import React from 'react';
import { format, isValid } from 'date-fns';
import constants from 'vets-json-schema/dist/constants.json';

import { makeSchemaForAllDisabilities } from '../../../../all-claims/utils/schemas';
import { isCompletingForm0781 } from '../../../../form0781/utils';
import {
  USA,
  VA_FACILITIES_ARRAY_PATH,
  VA_FACILITY_TYPE_LABELS,
} from '../../../constants';

export const STATE_LABELS = constants.formProfileStates.reduce(
  (labels, { value, label }) => ({ ...labels, [value]: label }),
  {},
);

/**
 * Returns the facility item for item-level `hideIf`/`required` callbacks.
 *
 * These callbacks also run during form-wide schema recalculation, where
 * `formData` is the full form data rather than the item, so the item is looked
 * up by index in either `formData` or `fullData`.
 *
 * @param {object} formData
 * @param {number} index - item index within `vaTreatmentFacilities`
 * @param {object} [fullData]
 * @returns {object} the item, or an empty object if none exists
 */
export const getVaFacilityItem = (formData, index, fullData) =>
  (formData?.[VA_FACILITIES_ARRAY_PATH] ??
    fullData?.[VA_FACILITIES_ARRAY_PATH])?.[index] || {};

/**
 * Labels of the conditions selected for a facility, in the order they appear
 * on the conditions page. Keys for conditions no longer claimed are ignored.
 */
export const getTreatedConditionNames = (item, fullData) => {
  const selected = item?.treatedDisabilityNames || {};
  const { properties = {} } = makeSchemaForAllDisabilities(fullData || {});
  return Object.keys(properties)
    .filter(key => selected[key] === true)
    .map(key => properties[key].title);
};

/**
 * Formats a "YYYY-MM" value as "Month YYYY" (e.g. "December 2020"). Returns
 * just the year when the month is missing, and "" when the year is invalid.
 */
export const formatMonthYear = value => {
  const [year, month] = (value || '').split('-');
  if (!/^\d{4}$/.test(year || '')) return '';
  if (!/^(0[1-9]|1[0-2])$/.test(month || '')) return year;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return isValid(date) ? format(date, 'MMMM yyyy') : '';
};

// "I'm not sure" is omitted from the card because it doesn't describe the
// facility; "Other" shows the Veteran's description when provided.
export const getFacilityTypeText = item => {
  const { facilityType, facilityTypeOther } = item || {};
  if (!facilityType || facilityType === 'notSure') return '';
  if (facilityType === 'other') {
    return facilityTypeOther?.trim() || VA_FACILITY_TYPE_LABELS.other;
  }
  return VA_FACILITY_TYPE_LABELS[facilityType] || '';
};

export const getLocationText = item => {
  const { city, state, country } = item?.treatmentCenterAddress || {};
  return [city, country === USA ? STATE_LABELS[state] || state : '', country]
    .filter(Boolean)
    .join(', ');
};

// The traumatic event answer is shown only while the Veteran is still
// completing the online 21-0781, since the question is skipped otherwise and
// an earlier answer may remain in the data.
export const showTraumaticEventLine = (item, fullData) =>
  item?.treatmentLocation0781Related === true &&
  isCompletingForm0781(fullData || {});

export const getTreatmentDetailsText = (item, fullData) =>
  [
    getTreatedConditionNames(item, fullData).join(', '),
    formatMonthYear(item?.treatmentDateRange?.from),
    showTraumaticEventLine(item, fullData)
      ? 'treatment related to traumatic events'
      : '',
  ]
    .filter(Boolean)
    .join('; ');

export const VaFacilityCardDescription = (item, _index, fullData) => {
  const lines = [
    getFacilityTypeText(item),
    getLocationText(item),
    getTreatmentDetailsText(item, fullData),
  ].filter(Boolean);

  return (
    <>
      {lines.map(line => (
        <p key={line} className="vads-u-margin-y--1">
          {line}
        </p>
      ))}
    </>
  );
};

export const isVaFacilityIncomplete = (item, fullData) => {
  const address = item?.treatmentCenterAddress || {};
  return (
    !item?.treatmentCenterName ||
    !address.country ||
    !address.city ||
    (address.country === USA && !address.state) ||
    !getTreatedConditionNames(item, fullData).length ||
    (isCompletingForm0781(fullData || {}) &&
      typeof item?.treatmentLocation0781Related !== 'boolean')
  );
};

export const vaMedicalRecordsOptions = {
  arrayPath: VA_FACILITIES_ARRAY_PATH,
  nounSingular: 'facility',
  nounPlural: 'VA or military health facilities',
  // Optional multi-page pattern (VADS "Multiple responses"): the chapter
  // already asks whether the Veteran has VA/military records before this loop
  // is reached, so the loop itself does not force at least one item. This also
  // means an empty summary page is a valid resting state rather than an error
  // that bounces the Veteran back to the intro.
  required: false,
  isItemIncomplete: isVaFacilityIncomplete,
  text: {
    getItemName: item => item?.treatmentCenterName,
    cardDescription: VaFacilityCardDescription,
    // The delete modal's wording is set out in full rather than left to the
    // platform defaults, which derive it from `nounSingular`/`nounPlural` —
    // renaming those for a summary heading would otherwise reword the modal.
    deleteTitle: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return itemName
        ? `Delete ${itemName}’s information?`
        : 'Delete this facility’s information?';
    },
    deleteDescription: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return itemName
        ? `We’ll delete the ${itemName} from your list of VA or military health facilities.`
        : `We’ll delete this facility from your list of VA or military health facilities.`;
    },
    deleteYes: () => 'Yes, delete this facility',
    deleteNo: () => 'No, keep this facility',
    alertItemDeleted: props => {
      const itemName = props.getItemName(
        props.itemData,
        props.index,
        props.formData,
      );
      return itemName
        ? `We’ve deleted ${itemName}’s information.`
        : `We’ve deleted this facility’s information.`;
    },
  },
};
